import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";

const instructors = [
  {
    id: 1,
    name: "Dr. Eyyad Al-Matny",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRVNN58XFDLxdqtwwWRSE924NjtuSryXFGxjg&s",
    description:
      "An English Language Teacher With A Ph.D. From A Prestigious University Is An Individual With Deep Expertise In English Linguistics, Literature, Or Language Education. This Advanced Academic Background Provides Them With Extensive Knowledge Of Language Structure, Cultural Context, And Teaching Methodologies. They Possess Strong Analytical And Communication Skills, Allowing Them To Deliver Complex Concepts In An Engaging And Understandable Way.",
  },
  {
    id: 2,
    name: "Dr. Sarah Johnson",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSDRCH9SbVgWLmf62LiL4kAFNcupmKVbBcAH593zPZFeNKJMNfQ4p1Ot6FGiJZ_vGZTJ8k&usqp=CAU",
    description:
      "With over 12 years of experience teaching Spanish, Dr. Johnson brings passion and expertise to every class. Her research in language acquisition has been published in leading academic journals, and her innovative teaching methods help students achieve fluency faster than traditional approaches.",
  },
  {
    id: 3,
    name: "Prof. Hiroshi Tanaka",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSERkGm1iRcKOBOwVJi8g9MZZRcLsJNPIEI4fDYnfQiqNz0Of_9usRI4JBRHYHV6WtXOB0&usqp=CAU",
    description:
      "A master of Japanese language instruction, Prof. Tanaka combines traditional teaching methods with modern technology to create an immersive learning experience. His background in linguistics and cultural studies provides students with deep insights into Japanese language and culture.",
  },
  {
    id: 4,
    name: "Dr. Maria Rodriguez",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSxIind5GkRhK5_AT0Oarc2ql4wAE6DvxTFcb35ALdBMuB5TXi1LsERrbnBQkqk0v8ojgk&usqp=CAU",
    description:
      "Specializing in Portuguese and Spanish, Dr. Rodriguez has helped hundreds of students achieve fluency through her structured yet flexible teaching approach. Her background in comparative linguistics allows her to help students make connections between related languages.",
  },
  {
    id: 5,
    name: "Prof. Jean Dupont",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQi_edvhg00woHCvqMEx576h33vcqPoHxgJBDFtOFuSoHlARzuckNzxGz9t738n2zO-PV8&usqp=CAU",
    description:
      "A native French speaker with a doctorate in language education, Prof. Dupont brings authentic pronunciation and cultural insights to his classes. His method emphasizes practical conversation skills while building a strong grammatical foundation.",
  },
].sort((a, b) => a.id - b.id);

const InstructorsSection = () => {
  const [currentInstructor, setCurrentInstructor] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInstructor((prev) =>
        prev === instructors.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Navigation functions remain unchanged
  const nextInstructor = () => {
    setCurrentInstructor((prev) =>
      prev === instructors.length - 1 ? 0 : prev + 1
    );
  };

  const prevInstructor = () => {
    setCurrentInstructor((prev) =>
      prev === 0 ? instructors.length - 1 : prev - 1
    );
  };

  const goToInstructor = (index) => {
    setCurrentInstructor(index);
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={sectionVariants}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto relative z-10">
        {/* Header remains unchanged */}
        <motion.div
          className="flex flex-col items-start mb-16 "
          variants={headerVariants}
        >
          <div className="flex items-center mb-3">
            <motion.div
              className="hidden sm:block bg-primary w-16 sm:w-20 md:w-28 h-6 xs:h-8 md:h-10 mr-4"
              initial={{ width: 0 }}
              animate={isInView ? { width: "7rem" } : { width: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              Our <span className="text-primary font-normal">Instructors</span>
            </h2>
          </div>
          <p className="text-gray-600 max-w-2xl ml-0 sm:ml-24 mt-3">
            Learn from our team of experienced language educators who are
            passionate about helping you achieve fluency.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-stretch justify-between gap-12 px-4 sm:px-6 lg:px-18">
          {/* Left side content remains unchanged */}
          <motion.div
            className="w-full lg:w-1/2 bg-gradient-to-tr from-gray-400 via-white to-gray-100 p-8 rounded-2xl shadow-lg"
            variants={contentVariants}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={instructors[currentInstructor].id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="w-full"
              >
                <div className="flex items-center mb-6">
                  <div className="w-1.5 h-16 bg-primary rounded-full mr-4"></div>
                  <motion.h3
                    className="text-2xl sm:text-3xl font-bold text-gray-800"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                  >
                    {instructors[currentInstructor].name}
                  </motion.h3>
                </div>

                <motion.div
                  className="bg-white/70 backdrop-blur-sm p-6 rounded-xl border border-gray-100 shadow-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  <motion.p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                    {instructors[currentInstructor].description}
                  </motion.p>
                </motion.div>

                <motion.div
                  className="mt-8 flex items-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                >
                  <span className="text-primary font-medium mr-3">
                    Languages:
                  </span>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      English
                    </span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      Arabic
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Right side - Image presentation */}
          <motion.div
            className="w-full lg:w-1/2 flex flex-col items-center"
            variants={imageVariants}
          >
            <div className="relative w-full max-w-md">
              {/* Decorative elements */}
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full"></div>

              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`image-${instructors[currentInstructor].id}`}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="relative z-10 w-full h-[400px] sm:h-[500px] overflow-hidden rounded-2xl shadow-xl"
                  >
                    {/* Image styling */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-b from-primary/20 to-primary/40"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.7, delay: 0.3 }}
                    ></motion.div>

                    <motion.img
                      src={instructors[currentInstructor].image}
                      alt={instructors[currentInstructor].name}
                      className="w-full h-full object-cover"
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.7 }}
                    />

                    {/* Instructor name overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                      <h4 className="text-white text-xl font-medium mb-2">
                        {instructors[currentInstructor].name}
                      </h4>
                      <div className="flex items-center">
                        <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm">
                          Language Expert
                        </span>
                      </div>
                    </div>

                    {/* Navigation arrows */}
                    <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 hover:opacity-100 transition-opacity duration-300">
                      <motion.button
                        onClick={prevInstructor}
                        className="bg-white/80 hover:bg-white text-primary p-3 rounded-full shadow-lg backdrop-blur-sm"
                        whileHover={{
                          scale: 1.1,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </motion.button>
                      <motion.button
                        onClick={nextInstructor}
                        className="bg-white/80 hover:bg-white text-primary p-3 rounded-full shadow-lg backdrop-blur-sm"
                        whileHover={{
                          scale: 1.1,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* UPDATED: Circular instructor images for navigation in ascending order by ID */}
              <div className="flex justify-center items-center mt-8 space-x-3">
                {instructors
                  .sort((a, b) => a.id - b.id) // Ensure instructors are sorted by ID
                  .map((instructor, index) => (
                    <motion.button
                      key={instructor.id}
                      onClick={() => goToInstructor(index)}
                      className={`relative overflow-hidden rounded-full transition-all duration-300 ${
                        currentInstructor === index
                          ? "ring-4 ring-primary ring-offset-2"
                          : "ring-2 ring-gray-200 hover:ring-gray-300"
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={`Go to instructor ${instructor.name}`}
                    >
                      <div
                        className={`relative ${
                          currentInstructor === index
                            ? "w-16 h-16 sm:w-20 sm:h-20"
                            : "w-10 h-10 sm:w-12 sm:h-12"
                        }`}
                      >
                        <img
                          src={instructor.image}
                          alt={instructor.name}
                          className="w-full h-full object-cover"
                        />
                        {currentInstructor === index && (
                          <div className="absolute inset-0 bg-primary/20"></div>
                        )}
                      </div>
                    </motion.button>
                  ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default InstructorsSection;
